import React, { useState, useEffect } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Spinner } from "@/components/common";
import { toast } from "sonner";
import { Download, FileText, Save, MessageSquarePlus, CheckCircle2, Loader2 } from "lucide-react";

export default function DocEditor({ docMeta, onSaved }) {
  const [doc, setDoc] = useState(null);
  const [sections, setSections] = useState([]);
  const [comment, setComment] = useState({ idx: 0, text: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const res = await api.get(`/documents/${docMeta.id}`);
    setDoc(res.data);
    setSections(res.data.content.sections || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [docMeta.id]);

  const updSection = (i, field, val) => {
    const arr = [...sections]; arr[i] = { ...arr[i], [field]: val }; setSections(arr);
  };

  const save = async () => {
    setSaving(true);
    try { await api.put(`/documents/${docMeta.id}`, { sections }); toast.success("Document saved"); onSaved?.(); }
    catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const addComment = async () => {
    if (!comment.text.trim()) return;
    const res = await api.post(`/documents/${docMeta.id}/comment`, { section_index: comment.idx, text: comment.text });
    setDoc({ ...doc, comments: [...(doc.comments || []), res.data] });
    setComment({ ...comment, text: "" });
    toast.success("Comment added");
  };

  const approve = async () => {
    try { await api.post(`/documents/${docMeta.id}/approve`, { version: "1.0" }); toast.success("Approved as v1.0"); load(); onSaved?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Approve failed (needs Engagement Lead/Admin)"); }
  };

  if (!doc) return <div className="flex-1 grid place-items-center"><Spinner label="Loading document…" /></div>;

  return (
    <div className="flex flex-col h-full">
      <div className="px-5 py-3 border-b flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-semibold truncate">{doc.title}</div>
          <div className="text-xs text-muted-foreground font-mono">{doc.reference} · {doc.status} v{doc.version}</div>
        </div>
        <div className="flex gap-2 mr-6 shrink-0">
          {doc.status !== "approved" && <Button size="sm" variant="ghost" className="gap-1.5 text-emerald-600" onClick={approve} data-testid="approve-doc"><CheckCircle2 size={14} /> Approve</Button>}
          <a href={`${API}/documents/${doc.id}/export?format=pdf`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="gap-1.5"><Download size={14} /> PDF</Button></a>
          <a href={`${API}/documents/${doc.id}/export?format=docx`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="gap-1.5"><Download size={14} /> DOCX</Button></a>
        </div>
      </div>

      <Tabs defaultValue="preview" className="flex-1 flex flex-col overflow-hidden">
        <TabsList className="mx-5 mt-3 self-start">
          <TabsTrigger value="preview" data-testid="doc-preview-tab">Preview</TabsTrigger>
          <TabsTrigger value="edit" data-testid="doc-edit-tab">Edit</TabsTrigger>
          <TabsTrigger value="comments" data-testid="doc-comments-tab">Comments {doc.comments?.length ? `(${doc.comments.length})` : ""}</TabsTrigger>
        </TabsList>

        <TabsContent value="preview" className="flex-1 overflow-hidden m-0 mt-3">
          <iframe title="doc" src={`${API}/documents/${doc.id}/export?format=html`} className="w-full h-full bg-white" />
        </TabsContent>

        <TabsContent value="edit" className="flex-1 overflow-y-auto px-5 py-3 space-y-4 m-0">
          {sections.map((s, i) => (
            <div key={i} className="rounded-lg border p-3">
              <Input value={s.heading || ""} onChange={(e) => updSection(i, "heading", e.target.value)} className="font-semibold mb-2" data-testid={`edit-heading-${i}`} />
              <Textarea rows={4} value={s.body || ""} onChange={(e) => updSection(i, "body", e.target.value)} data-testid={`edit-body-${i}`} />
              {s.table?.columns && <div className="text-xs text-muted-foreground mt-1">Contains a table with {s.table.rows?.length || 0} rows (preserved).</div>}
            </div>
          ))}
          <div className="sticky bottom-0 bg-background pt-2">
            <Button onClick={save} disabled={saving} className="gap-2 w-full" data-testid="save-doc">{saving ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />} Save changes</Button>
          </div>
        </TabsContent>

        <TabsContent value="comments" className="flex-1 overflow-y-auto px-5 py-3 space-y-3 m-0">
          <div className="flex gap-2 items-end">
            <div className="w-40">
              <label className="text-xs text-muted-foreground">Section</label>
              <select value={comment.idx} onChange={(e) => setComment({ ...comment, idx: Number(e.target.value) })} className="w-full border rounded-md h-9 text-sm bg-background px-2">
                {sections.map((s, i) => <option key={i} value={i}>{s.heading?.slice(0, 24) || `Section ${i + 1}`}</option>)}
              </select>
            </div>
            <Input placeholder="Add a review comment…" value={comment.text} onChange={(e) => setComment({ ...comment, text: e.target.value })} data-testid="comment-input" />
            <Button onClick={addComment} className="gap-1.5" data-testid="add-comment"><MessageSquarePlus size={15} /> Add</Button>
          </div>
          {(doc.comments || []).length === 0 && <p className="text-sm text-muted-foreground">No comments yet.</p>}
          {(doc.comments || []).map((c) => (
            <div key={c.id} className="rounded-lg border p-3 text-sm">
              <div className="flex items-center justify-between"><span className="font-medium">{c.author}</span><span className="text-xs text-muted-foreground">{sections[c.section_index]?.heading?.slice(0, 30) || `Section ${c.section_index + 1}`}</span></div>
              <p className="text-foreground mt-1">{c.text}</p>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
